import * as React from "react";
import { LayoutChangeEvent, TouchableOpacity } from "react-native";
import styled from "styled-components/native";

import {
  background,
  color,
  flex,
  FontSize,
  Greedy,
  Label,
  outlineColor,
  rounded,
  Background,
  Color,
} from "../../styled-component";
import { Theme } from "../../styles";
import { ColorProp } from "../../styles/styleTypes";

const ButtonContainer = styled.TouchableOpacity<
  {
    background: string;
    borderColor: string;
  } & Greedy
>`
  ${background};
  ${color};
  ${outlineColor};
  ${flex};
  ${rounded};

  border-color: ${(props) => props.borderColor};
  border-width: ${Theme.borderThickness}px;
  border-style: solid;
  padding: 0 ${Theme.spacing}px;
  justify-content: center;
`;

const ButtonText = styled(Label).attrs({
  fontSize: FontSize.Normal,
})<Background | Color>`
  text-align: center;
`;

export interface ButtonProps {
  onPress?: () => void;
  mode?: "contained" | "outlined" | "text";
  color?: ColorProp;
  title?: string;
  greedy?: boolean;
  disabled?: boolean;
  children?: React.ReactNode | ((props: Background & Color) => JSX.Element);
  onLayout?: (event: LayoutChangeEvent) => void;
  selected?: boolean;
  roundness?: number;
  background?: string;
}

// type Button = React.ForwardRefExoticComponent<
//   ButtonProps & React.RefAttributes<TouchableOpacity>
// > & { Text: typeof ButtonText };

const Button = React.forwardRef<TouchableOpacity, ButtonProps>(
  (
    {
      title,
      onPress,
      mode = "text",
      color: colorProp,
      greedy,
      disabled: isDisabled,
      children,
      onLayout,
      selected: isSelected,
      background: backgroundProp,
      ...rest
    },
    ref
  ) => {
    const isPrimary = colorProp === "primary";
    const isAccent = colorProp === "accent";
    const isContained = mode === "contained";
    const isText = mode === "text";

    const themeColor = React.useMemo(() => {
      if (isAccent) {
        return Theme.colors.accent;
      }

      return Theme.colors.primary;
    }, [isAccent]);

    const backgroundColor = React.useMemo(() => {
      if (backgroundProp) {
        return backgroundProp;
      }

      if (isSelected) {
        return Theme.colors.accentLight;
      }

      if (isDisabled) {
        return Theme.colors.backgroundDisabled;
      }

      return isContained ? themeColor : Theme.colors.background;
    }, [isContained, isDisabled, themeColor, isSelected, backgroundProp]);

    const textProps = React.useMemo(() => {
      if (isDisabled) {
        return { color: Theme.colors.textDisabled };
      }

      return isContained
        ? { background: backgroundColor }
        : { color: themeColor };
    }, [isDisabled, isContained, backgroundColor, themeColor]);

    const borderColor = React.useMemo(() => {
      if (backgroundProp) {
        return backgroundProp;
      }

      if (isDisabled) {
        return backgroundColor;
      }

      return isText ? backgroundColor : themeColor;
    }, [isText, isDisabled, backgroundColor, themeColor, backgroundProp]);

    const renderChildren = () => {
      if (children && typeof children === "function") {
        return children(textProps);
      }

      return children || <ButtonText {...textProps}>{title}</ButtonText>;
    };

    return (
      <ButtonContainer
        ref={ref}
        onPress={onPress}
        background={backgroundColor}
        borderColor={borderColor}
        greedy={greedy}
        disabled={isDisabled}
        onLayout={onLayout}
        {...rest}
      >
        {renderChildren()}
      </ButtonContainer>
    );
  }
);

type Button = typeof Button & { Text: typeof ButtonText };

(Button as Button).Text = ButtonText;

export default Button as Button;
